// src/lib/onboarding-context.ts
// Where did this player come from? Written by every door before /login, read by
// /claim and /welcome, and rebuilt from the URL when the email link opens in a
// different browser. Cleared when the funnel completes.

export const ONBOARDING_KEY = "scoreglass.onboarding";
export const ONBOARDING_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type OnboardingDoor = "community" | "event" | "ladder" | "home" | "invite" | "organizer";
const DOORS: readonly OnboardingDoor[] = ["community", "event", "ladder", "home", "invite", "organizer"];

export interface OnboardingContext {
  community?: string;
  door: OnboardingDoor;
  returnTo?: string;
  inviteToken?: string;
  /** Why the last invite could not be applied; shown once on /claim. */
  inviteError?: string;
  email?: string;
  mode?: "confirm" | "reset";
  startedAt: string;
}

const safeGet = (): string | null => { try { return localStorage.getItem(ONBOARDING_KEY); } catch { return null; } };
const safeSet = (v: string) => { try { localStorage.setItem(ONBOARDING_KEY, v); } catch { /* private mode */ } };
const safeRemove = () => { try { localStorage.removeItem(ONBOARDING_KEY); } catch { /* ignore */ } };

/** Only same-origin paths may be used as a return target. */
const isSafePath = (p: unknown): p is string => typeof p === "string" && p.startsWith("/") && !p.startsWith("//");
/** Invite tokens are opaque ids; accept only a short, URL-safe string. */
const isSafeToken = (t: unknown): t is string => typeof t === "string" && /^[A-Za-z0-9_-]{8,128}$/.test(t);

export function writeOnboarding(ctx: Omit<OnboardingContext, "startedAt">): OnboardingContext {
  const full: OnboardingContext = { ...ctx, startedAt: new Date().toISOString() };
  safeSet(JSON.stringify(full));
  return full;
}

export function readOnboarding(now: number = Date.now()): OnboardingContext | null {
  const raw = safeGet();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<OnboardingContext>;
    if (!parsed || typeof parsed.startedAt !== "string" || !DOORS.includes(parsed.door as OnboardingDoor)) {
      safeRemove();
      return null;
    }
    if (now - new Date(parsed.startedAt).getTime() > ONBOARDING_MAX_AGE_MS) {
      safeRemove();
      return null;
    }
    return parsed as OnboardingContext;
  } catch {
    safeRemove();
    return null;
  }
}

export function patchOnboarding(patch: Partial<Omit<OnboardingContext, "startedAt">>): OnboardingContext {
  const current = readOnboarding() ?? writeOnboarding({ door: "home" });
  const next: OnboardingContext = { ...current, ...patch };
  safeSet(JSON.stringify(next));
  return next;
}

export function clearOnboarding(): void { safeRemove(); }

export function onboardingFromSearchParams(params: URLSearchParams): OnboardingContext | null {
  const community = params.get("community") ?? undefined;
  const returnTo = params.get("returnTo");
  const door = params.get("door");
  const invite = params.get("invite");
  if (!community && !returnTo && !door && !invite) return null;
  return {
    door: DOORS.includes(door as OnboardingDoor) ? (door as OnboardingDoor) : "home",
    community,
    returnTo: isSafePath(returnTo) ? returnTo : undefined,
    inviteToken: isSafeToken(invite) ? invite : undefined,
    startedAt: new Date().toISOString(),
  };
}

/** The emailRedirectTo / OAuth redirectTo target, carrying the context as query params. */
export function buildCallbackUrl(origin: string, ctx: OnboardingContext | null): string {
  const url = new URL("/auth/callback", origin);
  if (ctx?.community) url.searchParams.set("community", ctx.community);
  if (ctx?.returnTo && isSafePath(ctx.returnTo)) url.searchParams.set("returnTo", ctx.returnTo);
  if (ctx?.door && ctx.door !== "home") url.searchParams.set("door", ctx.door);
  if (ctx?.inviteToken && isSafeToken(ctx.inviteToken)) url.searchParams.set("invite", ctx.inviteToken);
  return url.toString();
}
